import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

import { Suspense } from '@sorare/core/src/atoms/Suspense';
import { lazy } from '@sorare/core/src/lib/retry';

import type { BuyConfirmationProviderStateProps } from './index.tsx';
import { BuyConfirmationProvider as BuyConfirmationContextProvider } from './index.tsx';

const BuyTokenConfirmationDialogById = lazy(
  async () =>
    import('#src/components/buyActions/BuyTokenConfirmationDialogById/index.tsx'),
  'BuyTokenConfirmationDialogById'
);

const BuyPrimaryBuyConfirmationDialogById = lazy(
  async () =>
    import('#src/components/primaryBuy/BuyPrimaryBuyConfirmationDialogById/index.tsx'),
  'BuyPrimaryBuyConfirmationDialogById'
);

type Props = {
  children?: ReactNode;
};

const BuyConfirmationDialog = (
  props:
    | (Omit<BuyConfirmationProviderStateProps, 'onClose'> & {
        showBuyingConfirmation: boolean;
        onClose: () => void;
      })
    | null
) => {
  const {
    tokenOfferId,
    primaryBuyId,
    primaryBuyConfirmationOptions,
    onClose,
    showBuyingConfirmation,
  } = props || { onClose: () => {} };

  if (tokenOfferId) {
    return (
      <BuyTokenConfirmationDialogById
        open={!!showBuyingConfirmation}
        tokenOfferId={tokenOfferId}
        onClose={onClose}
      />
    );
  }
  if (primaryBuyId && primaryBuyConfirmationOptions) {
    return (
      <BuyPrimaryBuyConfirmationDialogById
        open={!!showBuyingConfirmation}
        primaryBuyId={primaryBuyId}
        options={primaryBuyConfirmationOptions}
        onClose={onClose}
      />
    );
  }
  return null;
};
export const BuyConfirmationProvider = ({ children }: Props) => {
  const [buyConfirmationProps, setBuyConfirmationProps] =
    useState<BuyConfirmationProviderStateProps | null>(null);

  const [showBuyingConfirmation, setShowBuyingConfirmation] =
    useState<boolean>(false);

  const { onClose: doOnClose, beforeOnClose } = buyConfirmationProps || {};

  const onClose = useCallback(() => {
    beforeOnClose?.();
    setBuyConfirmationProps(null);
    setShowBuyingConfirmation(false);
    doOnClose?.();
  }, [beforeOnClose, doOnClose]);

  return (
    <BuyConfirmationContextProvider
      value={{
        showBuyingConfirmation,
        setBuyConfirmationProps,
        setShowBuyingConfirmation,
        buyConfirmationProps,
      }}
    >
      <Suspense fallback={null}>
        <BuyConfirmationDialog
          {...buyConfirmationProps}
          showBuyingConfirmation={showBuyingConfirmation}
          onClose={onClose}
        />
      </Suspense>
      {children}
    </BuyConfirmationContextProvider>
  );
};
